import { jsx as _jsx } from "react/jsx-runtime";
/**
 * SignupContext - Provides a shared state store for form data across the signup flow
 *
 * This context serves as a single source of truth for form data and API client.
 * The useSignup hook will use this context to create the unified signup interface.
 *
 * @see useSignup - The hook to consume this context
 * @see SignupProvider - The provider component for this context
 */
import { createContext, useContext, useEffect, useMemo, useState, useCallback, } from "react";
import NeloApiClient from "./clients/NeloApiClient";
import NeloHeaders from "./utils/Headers";
import "./utils/i18n"; // Import i18n configuration
import i18n from "i18next";
import { useTranslation as useI18nTranslation } from "react-i18next";
/**
 * React context for sharing signup state
 */
const SignupContext = createContext(null);
/**
 * Provider component for the SignupContext
 *
 * Initializes the API client and shared state for the signup flow.
 *
 * @param props Provider configuration
 * @returns A context provider
 */
/**
 * Helper function to check if sessionStorage is available
 * This avoids errors in environments where sessionStorage is not available
 */
const isSessionStorageAvailable = () => {
    if (typeof window === "undefined")
        return false;
    try {
        const testKey = "__storage_test__";
        window.sessionStorage.setItem(testKey, testKey);
        window.sessionStorage.removeItem(testKey);
        return true;
    }
    catch (e) {
        return false;
    }
};
/**
 * Storage key for signup state
 */
const SIGNUP_STORAGE_KEY = "nelo_signup_state";
export const SignupProvider = ({ deviceInfo, deviceState: initialDeviceState, neloApiBaseUrl, analyticsFn, temporaryUserUuid = "", advertisingId, accessToken, language = "es", sessionId, useSessionStorage = false, children, smsOnly = false, }) => {
    // Set initial language
    const [currentLanguage, setLanguage] = useState(language);
    // Initialize i18next with the specified language
    useMemo(() => {
        i18n.changeLanguage(currentLanguage);
    }, [currentLanguage]);
    // Check if we should/can use sessionStorage
    const canUseStorage = useMemo(() => useSessionStorage && isSessionStorageAvailable(), [useSessionStorage]);
    // Load initial state from sessionStorage if available
    const getInitialState = (key, defaultValue) => {
        if (!canUseStorage)
            return defaultValue;
        try {
            const storedData = window.sessionStorage.getItem(SIGNUP_STORAGE_KEY);
            if (!storedData)
                return defaultValue;
            const parsedData = JSON.parse(storedData);
            return parsedData[key] !== undefined ? parsedData[key] : defaultValue;
        }
        catch (e) {
            console.error("Failed to load state from sessionStorage:", e);
            return defaultValue;
        }
    };
    // Shared state across the signup flow - single source of truth for form values
    // Basic account details
    const [phoneNumber, setPhoneNumber] = useState(() => getInitialState("phoneNumber", ""));
    const [email, setEmail] = useState(() => getInitialState("email", ""));
    const [verificationCode, setVerificationCode] = useState(""); // Never persist verification code
    // CURP and personal information
    const [curpDetails, setCurpDetails] = useState(() => getInitialState("curpDetails", {
        firstName: "",
        paternalLastName: "",
        maternalLastName: "",
        gender: "",
        dayOfBirth: "",
        monthOfBirth: "",
        yearOfBirth: "",
        stateOfBirth: "",
    }));
    const [curp, setCurp] = useState(() => getInitialState("curp", ""));
    // Employment information
    const [occupation, setOccupation] = useState(() => getInitialState("occupation", ""));
    const [income, setIncome] = useState(() => getInitialState("income", 0));
    // Address information
    const [address, setAddress] = useState(() => getInitialState("address", {
        street: "",
        buildingNumber: "",
        interiorNumber: "",
        colony: "",
        city: "",
        state: "",
        postalCode: "",
        placeId: null,
    }));
    // Security and transaction information
    const [pin, setPin] = useState(() => getInitialState("pin", ""));
    // Referral data (undefined by default)
    const [referralData, setReferralData] = useState(() => getInitialState("referralData", undefined));
    // Device and session tracking
    const [deviceState, setDeviceState] = useState(() => getInitialState("deviceState", initialDeviceState || {}));
    const [temporaryUuid, setTemporaryUuid] = useState(() => getInitialState("temporaryUuid", temporaryUserUuid));
    const [currentSessionId, setSessionId] = useState(() => getInitialState("sessionId", sessionId));
    // State tracking for sessionStorage loading
    const [loadedFromStorage, setLoadedFromStorage] = useState(() => {
        // Check if any data was loaded from sessionStorage
        if (!canUseStorage)
            return false;
        try {
            const storedData = window.sessionStorage.getItem(SIGNUP_STORAGE_KEY);
            return !!storedData; // true if data exists, false otherwise
        }
        catch (e) {
            return false;
        }
    });
    // Function to change the language
    const changeLanguage = useCallback((lng) => {
        i18n.changeLanguage(lng);
        setLanguage(lng);
    }, []);
    // Get translation function
    const { t } = useI18nTranslation();
    // Initialize API client
    const apiClient = useMemo(() => {
        const neloHeaders = new NeloHeaders(deviceInfo);
        const client = new NeloApiClient(neloHeaders, neloApiBaseUrl, analyticsFn, temporaryUserUuid, advertisingId, accessToken);
        // Initialize session ID
        client.setSessionId(sessionId);
        return client;
    }, [
        deviceInfo,
        neloApiBaseUrl,
        analyticsFn,
        temporaryUserUuid,
        advertisingId,
        accessToken,
        sessionId,
    ]);
    // Group form values and setters
    const formValues = useMemo(() => ({
        phoneNumber,
        email,
        verificationCode,
        curpDetails,
        curp,
        occupation,
        income,
        address,
        pin,
        referralData,
        deviceState,
        temporaryUuid,
        sessionId: currentSessionId,
        language: currentLanguage,
        loadedFromStorage,
    }), [
        phoneNumber,
        email,
        verificationCode,
        curpDetails,
        curp,
        occupation,
        income,
        address,
        pin,
        referralData,
        deviceState,
        temporaryUuid,
        currentSessionId,
        currentLanguage,
        loadedFromStorage,
    ]);
    const setFormValues = useMemo(() => ({
        setPhoneNumber,
        setEmail,
        setVerificationCode,
        setCurpDetails,
        setCurp,
        setOccupation,
        setIncome,
        setAddress,
        setPin,
        setReferralData,
        setDeviceState,
        setTemporaryUuid,
        setSessionId,
        setLanguage,
    }), []);
    // Persist state to sessionStorage when it changes
    useEffect(() => {
        if (!canUseStorage)
            return;
        // Create a new state object with everything except verificationCode
        const stateToStore = {
            phoneNumber,
            email,
            curpDetails,
            curp,
            occupation,
            income,
            address,
            pin,
            referralData,
            deviceState,
            temporaryUuid,
            sessionId: currentSessionId,
            language: currentLanguage,
        };
        try {
            window.sessionStorage.setItem(SIGNUP_STORAGE_KEY, JSON.stringify(stateToStore));
        }
        catch (e) {
            console.error("Failed to persist state to sessionStorage:", e);
        }
    }, [
        canUseStorage,
        phoneNumber,
        email,
        curpDetails,
        curp,
        occupation,
        income,
        address,
        pin,
        referralData,
        deviceState,
        temporaryUuid,
        currentSessionId,
        currentLanguage,
    ]);
    // Memoize context value to prevent unnecessary re-renders
    const contextValue = useMemo(() => ({
        apiClient,
        formValues,
        setFormValues,
        changeLanguage,
        t,
        smsOnly,
    }), [apiClient, formValues, changeLanguage, t, smsOnly]);
    return (_jsx(SignupContext.Provider, { value: contextValue, children: children }));
};
/**
 * Hook to access the signup context
 *
 * Provides access to form state, API client, and translation functions.
 *
 * Example usage with translations:
 * ```tsx
 * const { t, changeLanguage } = useSignup();
 *
 * // Get an error message
 * const errorMsg = t('errors.email.invalid');
 *
 * // Change language
 * changeLanguage('es');
 * ```
 *
 * @returns The signup context value including translation functions
 * @throws Error if used outside a SignupProvider
 */
export const useSignup = () => {
    const context = useContext(SignupContext);
    if (!context) {
        throw new Error("useSignup must be used within a SignupProvider");
    }
    return context;
};
